import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Users, Eye,
  Loader2, Mail, AlertCircle, Variable, Search, X,
  Monitor, Smartphone, Info, BookOpen, CheckCircle,
  ChevronLeft, ChevronRight, Globe, Zap, CreditCard
} from 'lucide-react';
import { campaignApi, templateApi, addressBookApi, senderProfileApi, enterpriseApi } from '../services/emailApi';
import { useToast } from '../shared/ToastProvider';
import TemplateWizardStepIndicator from '../templates/TemplateWizardStepIndicator';
import { useWallet } from '../../../context/walletContext';
import './EmailCampaignCreate.css';

/* ═══════════════════════════════════════════ */
/*  CONSTANTS                                  */
/* ═══════════════════════════════════════════ */
const STEPS = [
  { key: 'info', label: 'Campaign Info' },
  { key: 'template', label: 'Select Template' },
  { key: 'audience', label: 'Address Book' },
  { key: 'mapping', label: 'Variables & Preview' },
  { key: 'review', label: 'Review' },
];

const INTERNAL_FIELDS = new Set([
  'key', 'id', 'userid', 'userdata', 'companyid',
  'createdon', 'updatedon', 'rowno',
]);
const FILTER_ONLY_FIELDS = new Set(['label']);

const CONTACTS_PER_PAGE = 50;

/* ═══════════════════════════════════════════ */
/*  Build email preview HTML                   */
/* ═══════════════════════════════════════════ */
const buildPreviewHtml = (template, mappingObj, firstContact, resolveValue) => {
  if (!template) return '';

  const replaceVars = (text) => {
    if (!text) return '';
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      const col = mappingObj[`{{${key}}}`];
      const val = col && firstContact ? resolveValue(firstContact, col) : '';
      if (val) return val;
      return `<span style="background:#e0e7ff;color:#4338ca;padding:1px 4px;border-radius:3px;font-size:0.85em">${match}</span>`;
    });
  };

  const logoUrl = template.headerLogoSignedUrl || template.headerLogoUrl || template.logoUrlPreview || '';
  const bannerUrl = template.headerBannerSignedUrl || template.headerBannerImageUrl || template.bannerImageUrlPreview || '';
  const footerImgUrl = template.footerImageSignedUrl || template.footerImageUrl || template.footerImageUrlPreview || '';
  const bgColor = template.headerBackgroundColor || (template.header && template.header.backgroundColor) || '#ffffff';
  const logoPos = template.headerLogoPosition || (template.header && template.header.logoPosition) || 'center';
  const social = template.socialLinks || (template.footer && template.footer.socialLinks) || {};

  let headerHtml = '';
  const hTitle = template.headerTitle || (template.header && template.header.title) || '';
  if (hTitle || logoUrl || bannerUrl) {
    headerHtml = '<div style="background-color:' + bgColor + ';padding:24px 20px;text-align:' + logoPos + '">';
    if (logoUrl) headerHtml += '<img src="' + logoUrl + '" alt="Logo" style="max-height:60px;display:' + (logoPos === 'center' ? 'block' : 'inline-block') + ';' + (logoPos === 'center' ? 'margin:0 auto' : logoPos === 'right' ? 'margin-left:auto' : '') + '">';
    if (hTitle) headerHtml += '<h1 style="margin:12px 0 0;font-size:22px;color:#1e293b">' + replaceVars(hTitle) + '</h1>';
    if (bannerUrl) headerHtml += '<img src="' + bannerUrl + '" alt="Banner" style="width:100%;max-width:600px;margin-top:16px;border-radius:8px;display:block;' + (logoPos === 'center' ? 'margin-left:auto;margin-right:auto' : '') + '">';
    headerHtml += '</div>';
  }

  let bodyHtml = '<div style="padding:24px 20px">';
  const greeting = template.bodyGreeting || (template.body && template.body.greeting) || '';
  const content = template.bodyContent || (template.body && template.body.content) || '';
  if (greeting) bodyHtml += '<p style="font-size:16px;color:#334155;margin:0 0 16px">' + replaceVars(greeting) + '</p>';
  bodyHtml += '<div style="color:#475569;line-height:1.7;font-size:15px">' + replaceVars(content || '<p style="color:#94a3b8;font-style:italic">No content</p>') + '</div>';

  // CTA Button
  const ctaText = template.ctaButtonText || (template.cta && template.cta.buttonText) || '';
  const ctaUrl = template.ctaButtonUrl || (template.cta && template.cta.buttonUrl) || '#';
  const ctaBg = template.ctaButtonBackgroundColor || (template.cta && template.cta.backgroundColor) || '#007bff';
  if (ctaText) {
    bodyHtml += '<div style="text-align:center;margin:20px 0 8px"><a href="' + ctaUrl + '" style="display:inline-block;padding:12px 28px;background:' + ctaBg + ';color:#fff;text-decoration:none;border-radius:6px;font-weight:600;font-size:15px">' + ctaText + '</a></div>';
  }

  // Attachments
  const attachments = template.bodyAttachments || (template.body && template.body.attachments) || [];
  if (attachments.length > 0) {
    bodyHtml += '<div style="margin-top:16px;padding-top:12px;border-top:1px solid #e5e7eb">';
    bodyHtml += '<p style="font-size:13px;color:#64748b;margin:0 0 8px;font-weight:600">📎 Attachments (' + attachments.length + ')</p>';
    attachments.forEach(function(att) {
      const name = att.name || att.fileName || 'Attachment';
      const size = att.size ? (att.size < 1024 ? att.size + ' B' : att.size < 1048576 ? (att.size / 1024).toFixed(1) + ' KB' : (att.size / 1048576).toFixed(1) + ' MB') : '';
      const icon = (att.type || '').includes('pdf') ? '📄' : (att.type || '').includes('image') ? '🖼️' : '📎';
      const downloadLink = att.downloadUrl || att.url || '';
      if (downloadLink) {
        bodyHtml += '<a href="' + downloadLink + '" target="_blank" rel="noopener noreferrer" style="display:flex;align-items:center;gap:8px;padding:8px 10px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;margin-bottom:6px;text-decoration:none;cursor:pointer;transition:background 0.2s,border-color 0.2s" onmouseover="this.style.background=\'#f1f5f9\';this.style.borderColor=\'#cbd5e1\'" onmouseout="this.style.background=\'#f8fafc\';this.style.borderColor=\'#e2e8f0\'">';
      } else {
        bodyHtml += '<div style="display:flex;align-items:center;gap:8px;padding:8px 10px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;margin-bottom:6px">';
      }
      bodyHtml += '<span style="font-size:18px;flex-shrink:0">' + icon + '</span>';
      bodyHtml += '<div style="min-width:0;flex:1"><div style="font-size:13px;color:#334155;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + name + '</div>';
      if (size) bodyHtml += '<div style="font-size:11px;color:#94a3b8">' + size + '</div>';
      bodyHtml += '</div>';
      if (downloadLink) bodyHtml += '<span style="font-size:14px;color:#64748b;flex-shrink:0">↗</span></a>';
      else bodyHtml += '</div>';
    });
    bodyHtml += '</div>';
  }

  bodyHtml += '</div>';

  let footerHtml = '';
  const sig = template.footerSignature || (template.footer && template.footer.signature) || '';
  const slogan = template.footerSlogan || (template.footer && template.footer.slogan) || '';
  const webUrl = template.footerWebsiteUrl || (template.footer && template.footer.websiteUrl) || '';
  const addr = template.footerCompanyAddress || (template.footer && template.footer.companyAddress) || '';
  const showUnsub = template.footerShowUnsubscribe != null ? template.footerShowUnsubscribe : (template.footer && template.footer.showUnsubscribe != null ? template.footer.showUnsubscribe : true);
  const hasFooter = sig || webUrl || slogan || addr || showUnsub || footerImgUrl || Object.values(social).some(Boolean);
  if (hasFooter) {
    footerHtml = '<div style="border-top:1px solid #e5e7eb;padding:24px 20px;text-align:center;background:#f8fafc">';
    if (footerImgUrl) footerHtml += '<img src="' + footerImgUrl + '" alt="" style="max-height:40px;margin-bottom:12px;display:block;margin-left:auto;margin-right:auto">';
    if (sig) footerHtml += '<p style="font-size:14px;color:#475569;margin:0 0 8px;white-space:pre-line">' + sig + '</p>';
    if (slogan) footerHtml += '<p style="font-size:13px;color:#94a3b8;font-style:italic;margin:0 0 12px">' + slogan + '</p>';
    var si = [];
    if (social.facebook) si.push('<a href="' + social.facebook + '" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">Facebook</a>');
    if (social.instagram) si.push('<a href="' + social.instagram + '" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">Instagram</a>');
    if (social.twitter) si.push('<a href="' + social.twitter + '" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">Twitter</a>');
    if (social.linkedin) si.push('<a href="' + social.linkedin + '" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">LinkedIn</a>');
    if (si.length) footerHtml += '<div style="margin:8px 0">' + si.join(' &middot; ') + '</div>';
    if (webUrl) footerHtml += '<p style="font-size:12px;margin:8px 0 0"><a href="' + webUrl + '" style="color:#4f46e5;text-decoration:none">' + webUrl + '</a></p>';
    if (addr) footerHtml += '<p style="font-size:12px;color:#94a3b8;margin:6px 0 0">' + addr + '</p>';
    if (showUnsub) footerHtml += '<p style="font-size:11px;margin:12px 0 0"><a href="#" style="color:#94a3b8;text-decoration:underline">Unsubscribe</a></p>';
    footerHtml += '</div>';
  }

  return '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;margin:0;padding:0;color:#1a1a2e;line-height:1.6;background:#f1f5f9}img{max-width:100%;height:auto;display:block;object-fit:contain}a{color:#4f46e5}.email-container{max-width:600px;margin:0 auto;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08);word-wrap:break-word}</style></head><body><div class="email-container">' + headerHtml + bodyHtml + footerHtml + '</div></body></html>';
};

/* ═══════════════════════════════════════════ */
/*  Append attachments to pre-rendered HTML    */
/* ═══════════════════════════════════════════ */
const appendAttachmentsToHtml = (html, template) => {
  if (!html || !template) return html || '';
  const attachments = template.bodyAttachments || (template.body && template.body.attachments) || [];
  if (attachments.length === 0) return html;

  // Build attachments HTML section
  let attHtml = '<div style="margin:16px 20px;padding-top:12px;border-top:1px solid #e5e7eb">';
  attHtml += '<p style="font-size:13px;color:#64748b;margin:0 0 8px;font-weight:600">📎 Attachments (' + attachments.length + ')</p>';
  attachments.forEach(function(att) {
    const name = att.name || att.fileName || 'Attachment';
    const size = att.size ? (att.size < 1024 ? att.size + ' B' : att.size < 1048576 ? (att.size / 1024).toFixed(1) + ' KB' : (att.size / 1048576).toFixed(1) + ' MB') : '';
    const icon = (att.type || '').includes('pdf') ? '📄' : (att.type || '').includes('image') ? '🖼️' : '📎';
    const downloadLink = att.downloadUrl || att.url || '';
    if (downloadLink) {
      attHtml += '<a href="' + downloadLink + '" target="_blank" rel="noopener noreferrer" style="display:flex;align-items:center;gap:8px;padding:8px 10px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;margin-bottom:6px;text-decoration:none;cursor:pointer;transition:background 0.2s,border-color 0.2s" onmouseover="this.style.background=\'#f1f5f9\';this.style.borderColor=\'#cbd5e1\'" onmouseout="this.style.background=\'#f8fafc\';this.style.borderColor=\'#e2e8f0\'">';
    } else {
      attHtml += '<div style="display:flex;align-items:center;gap:8px;padding:8px 10px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;margin-bottom:6px">';
    }
    attHtml += '<span style="font-size:18px;flex-shrink:0">' + icon + '</span>';
    attHtml += '<div style="min-width:0;flex:1"><div style="font-size:13px;color:#334155;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + name + '</div>';
    if (size) attHtml += '<div style="font-size:11px;color:#94a3b8">' + size + '</div>';
    attHtml += '</div>';
    if (downloadLink) attHtml += '<span style="font-size:14px;color:#64748b;flex-shrink:0">↗</span></a>';
    else attHtml += '</div>';
  });
  attHtml += '</div>';

  // Insert attachments before the footer or end of container
  // Look for footer (border-top with background:#f8fafc) or closing tags
  const footerMatch = html.match(/<div style="border-top:1px solid #e5e7eb;padding:24px 20px;text-align:center;background:#f8fafc">/);
  if (footerMatch && footerMatch.index !== undefined) {
    return html.slice(0, footerMatch.index) + attHtml + html.slice(footerMatch.index);
  }
  // Fallback: insert before </div></body></html>
  const endMatch = html.match(/<\/div><\/body><\/html>\s*$/i);
  if (endMatch && endMatch.index !== undefined) {
    return html.slice(0, endMatch.index) + attHtml + html.slice(endMatch.index);
  }
  // Last fallback: append at end
  return html + attHtml;
};

/* ═══════════════════════════════════════════ */
/*  EmailCampaignCreate                        */
/* ═══════════════════════════════════════════ */
const EmailCampaignCreate = () => {
  const navigate = useNavigate();
  const { id: campaignId } = useParams();
  const isEditMode = Boolean(campaignId);
  const toast = useToast();
  const {
    planStatus: planQuota,
    loadingPlanStatus: planQuotaLoading,
  } = useWallet();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  /* Ref to prevent template-variables useEffect from wiping mapping
     that was already restored from an existing campaign (edit mode) */
  const preserveMappingRef = useRef(false);

  /* ── Campaign info ──────────────────────── */
  const [form, setForm] = useState({ name: '', scheduleTime: '', fromEmail: '', fromName: '' });

  /* ── Sending / From address ─────────────── */
  const [fromPreview, setFromPreview] = useState(null);
  const [sendingDomain, setSendingDomain] = useState('shared'); // 'shared' | 'custom'
  const [customDomains, setCustomDomains] = useState([]);      // all domains from listDomains
  const [selectedCustomDomain, setSelectedCustomDomain] = useState(null); // selected verified domain object

  /* ── Template selection ─────────────────── */
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [tplPage, setTplPage] = useState(1);
  const [tplPagination, setTplPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [tplListLoading, setTplListLoading] = useState(false);

  /* ── Audience ───────────────────────────── */
  const [addressBooks, setAddressBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState('');
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectionType, setSelectionType] = useState('all');
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [contactSearch, setContactSearch] = useState('');
  const [contactPage, setContactPage] = useState(1);

  /* ── Labels ─────────────────────────────── */
  const [labels, setLabels] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [labelsLoading, setLabelsLoading] = useState(false);

  /* ── Variable mapping ───────────────────── */
  const [templateVariables, setTemplateVariables] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [variableType, setVariableType] = useState('name');
  const [previewMode, setPreviewMode] = useState('desktop');

  /* ── Loading state ──────────────────────── */
  const [loadingLists, setLoadingLists] = useState(true);

  /* ── Preview iframe ref ── */
  const previewIframeRef = useRef(null);

  /* ════════════════════════════════════════════ */
  /* Fetch existing campaign in EDIT mode         */
  /* ════════════════════════════════════════════ */
  useEffect(() => {
    if (!isEditMode || !campaignId) return;
    
    const fetchCampaign = async () => {
      setEditLoading(true);
      try {
        const res = await campaignApi.getById(campaignId);
        if (res.data?.success && res.data?.data) {
          const c = res.data.data;
          
          // Populate form fields
          setForm({
            name: c.name || '',
            scheduleTime: c.schedule_time ? new Date(c.schedule_time).toISOString().slice(0, 16) : '',
            fromEmail: c.from_email || '',
            fromName: c.from_name || '',
          });
          
          // Address book & selection
          if (c.addressbook_id) setSelectedBookId(c.addressbook_id);
          if (c.selection_type) setSelectionType(c.selection_type);
          if (c.selected_userids && Array.isArray(c.selected_userids)) {
            setSelectedUserIds(new Set(c.selected_userids));
          }
          
          // Template mapping
          if (c.template_mapping) {
            if (c.template_mapping.mapping) setMapping(c.template_mapping.mapping);
            if (c.template_mapping.variabletype) setVariableType(c.template_mapping.variabletype);
          }
          
          // Template - fetch via preview API (returns rendered HTML + signed URLs)
          if (c.template_id) {
            try {
              const tplRes = await templateApi.previewById(c.template_id);
              if (tplRes.data?.success) {
                const d = tplRes.data?.data;
                // Signal that the next template-variables useEffect run should
                // preserve the mapping we just loaded from the campaign API
                preserveMappingRef.current = true;
                if (typeof d === 'string') {
                  setSelectedTemplate({ id: c.template_id, html: d });
                } else if (d && typeof d === 'object') {
                  setSelectedTemplate(d);
                } else if (tplRes.data?.html) {
                  setSelectedTemplate({ id: c.template_id, html: tplRes.data.html });
                }
                // Note: Variable extraction is handled by the useEffect on selectedTemplate change
                // Don't manually set templateVariables here as useEffect will handle all field paths
              }
            } catch { /* template fetch failed */ }
          }
        } else {
          toast.error('Campaign not found');
          navigate('/dashboard/email/campaigns');
        }
      } catch (err) {
        toast.error(err?.response?.data?.error || 'Failed to load campaign');
        navigate('/dashboard/email/campaigns');
      } finally {
        setEditLoading(false);
      }
    };
    
    fetchCampaign();
  }, [isEditMode, campaignId, navigate, toast]);

  /* ════════════════════════════════════════════ */
  /* Fetch sender profile + domain list          */
  /* (+ campaign data for domain pre-selection)   */
  /* ════════════════════════════════════════════ */
  useEffect(() => {
    (async () => {
      try {
        // Fetch sender profile, domain list, and campaign (in edit mode) in parallel
        const fetches = [
          senderProfileApi.get().catch(() => null),
          enterpriseApi.listDomains().catch(() => null),
        ];
        // In edit/clone mode, also fetch the campaign so we can restore its domain selection
        if (isEditMode && campaignId) {
          fetches.push(campaignApi.getById(campaignId).catch(() => null));
        }
        const [profileRes, domainsRes, campaignRes] = await Promise.all(fetches);

        // --- Sender Profile ---
        if (profileRes?.data?.success) {
          const d = profileRes.data.data;
          const sharedDomain = d.sharedDomain || 'mail.messageos.io';
          const handle = d.senderHandle || 'info';
          const sharedFrom = d.senderName
            ? `${d.senderName} <${handle}@${sharedDomain}>`
            : `${handle}@${sharedDomain}`;

          setFromPreview({
            fromAddress: d.fromPreview || sharedFrom,
            sharedFrom,
            senderName: d.senderName || '',
            senderHandle: handle,
            replyToEmail: d.replyToEmail || '',
            sharedDomain,
          });

        }

        // --- Resolve which domain the campaign was using (edit/clone mode) ---
        let campaignDomain = null;
        if (campaignRes?.data?.success && campaignRes.data?.data) {
          const c = campaignRes.data.data;
          if (c.sending_mode === 'verified_domain' && c.from_email) {
            campaignDomain = c.from_email.split('@')[1] || null;
          }
        }

        // --- Domain List ---
        if (domainsRes?.data?.success) {
          const domainList = domainsRes.data.data || [];
          setCustomDomains(domainList);

          // In edit/clone mode, pre-select the domain the campaign was originally using
          if (campaignDomain) {
            const matchedDomain = domainList.find(dm =>
              dm.domain === campaignDomain &&
              (dm.verificationStatus === 'Success' || dm.verificationStatus === 'verified'));
            if (matchedDomain) {
              setSelectedCustomDomain(matchedDomain);
              setSendingDomain('custom');
            } else {
              // Campaign's domain no longer verified — fall through to active domain below
              campaignDomain = null;
            }
          }

          // If no campaign domain matched (new campaign or domain unavailable), auto-select active
          if (!campaignDomain) {
            const activeDomain = domainList.find(dm => dm.isActive &&
              (dm.verificationStatus === 'Success' || dm.verificationStatus === 'verified'));
            if (activeDomain) {
              setSelectedCustomDomain(activeDomain);
              setSendingDomain('custom');
            } else {
              const firstVerified = domainList.find(dm =>
                dm.verificationStatus === 'Success' || dm.verificationStatus === 'verified');
              if (firstVerified) {
                setSelectedCustomDomain(firstVerified);
              }
            }
          }
        }
      } catch { /* non-critical */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ════════════════════════════════════════════ */
  /* Fetch address books on mount                 */
  /* ════════════════════════════════════════════ */
  useEffect(() => {
    (async () => {
      try {
        const abRes = await addressBookApi.list();
        if (abRes.data?.success || abRes.data?.entity) {
          setAddressBooks(abRes.data?.data || abRes.data?.entity || []);
        }
      } catch {
        toast.error('Failed to load address books');
      } finally {
        setLoadingLists(false);
      }
    })();
  }, [toast]);

  /* ════════════════════════════════════════════ */
  /* Fetch templates — server-side paginated      */
  /* ════════════════════════════════════════════ */
  const fetchTemplates = useCallback(async (page = 1) => {
    setTplListLoading(true);
    try {
      const params = { page, limit: tplPagination.limit, status: 'active' };
      if (templateSearch.trim()) params.search = templateSearch.trim();
      const res = await templateApi.list(params);
      if (res.data?.success) {
        const d = res.data.data;
        setTemplates(Array.isArray(d) ? d : d?.templates || d?.rows || []);
        const pag = res.data.pagination || d?.pagination || {};
        setTplPagination(prev => ({
          ...prev,
          page: pag.page || page,
          total: pag.total || pag.totalCount || 0,
          totalPages: pag.totalPages || Math.ceil((pag.total || pag.totalCount || 0) / prev.limit) || 1,
        }));
      }
    } catch {
      toast.error('Failed to load templates');
    } finally { setTplListLoading(false); }
  }, [templateSearch, tplPagination.limit, toast]);

  useEffect(() => {
    const timer = setTimeout(() => fetchTemplates(templateSearch ? 1 : tplPage), templateSearch ? 400 : 0);
    return () => clearTimeout(timer);
  }, [tplPage, templateSearch, fetchTemplates]);

  /* ════════════════════════════════════════════ */
  /* Fetch labels when address book selected     */
  /* ════════════════════════════════════════════ */
  useEffect(() => {
    if (!selectedBookId) { setLabels([]); return; }
    (async () => {
      setLabelsLoading(true);
      try {
        const res = await addressBookApi.getLabels({ bookid: selectedBookId });
        if (
          (res.data?.status === true || res.data?.statuscode === 200) &&
          res.data?.entity?.labels
        ) {
          const lbls = res.data.entity.labels;
          setLabels(Array.isArray(lbls) ? lbls.sort() : []);
        } else {
          setLabels([]);
        }
      } catch {
        // Fallback: extract labels from loaded contacts
        if (contacts.length > 0) {
          const uniqueLabels = [...new Set(contacts.map(c => c.label).filter(Boolean))];
          setLabels(uniqueLabels.sort());
        } else {
          setLabels([]);
        }
      } finally { setLabelsLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBookId]);

  /* ════════════════════════════════════════════ */
  /* Load contacts — server-side paginated       */
  /* ════════════════════════════════════════════ */
  useEffect(() => {
    if (!selectedBookId) { setContacts([]); setContactsTotal(0); return; }
    const fetchContacts = async () => {
      setContactsLoading(true);
      try {
        const res = await addressBookApi.getUsers({
          bookid: selectedBookId,
          limit: CONTACTS_PER_PAGE,
          offset: (contactPage - 1) * CONTACTS_PER_PAGE,
          searchby: contactSearch.trim(),
          labelfilter: selectedLabel,
        });
        const entity = res.data?.entity || res.data?.data || {};
        const contactsData = Array.isArray(entity) ? entity : (entity.users || []);
        const total = entity.totalRecords || entity.totalCount || res.data?.totalcount || contactsData.length;
        setContacts(contactsData);
        setContactsTotal(total);
      } catch {
        toast.error('Failed to load contacts');
      } finally { setContactsLoading(false); }
    };
    const timer = setTimeout(fetchContacts, contactSearch ? 400 : 0);
    return () => clearTimeout(timer);
  }, [selectedBookId, contactPage, contactSearch, selectedLabel, toast]);

  /* ════════════════════════════════════════════ */
  /* Build available columns from first contact  */
  /* ════════════════════════════════════════════ */
  useEffect(() => {
    if (contacts.length === 0) { setAvailableColumns([]); return; }
    const sample = contacts[0];
    const cols = [];
    const addedKeys = new Set();

    Object.keys(sample).forEach(rawKey => {
      const key = rawKey.toLowerCase();
      if (INTERNAL_FIELDS.has(key) || FILTER_ONLY_FIELDS.has(key)) return;
      if (typeof sample[rawKey] === 'object' && sample[rawKey] !== null) return;

      if (key === 'email' || key === 'emailid') {
        if (!addedKeys.has('emailid')) {
          cols.push({ value: 'emailid', label: 'Email' });
          addedKeys.add('emailid');
        }
        return;
      }
      if (!addedKeys.has(rawKey)) {
        cols.push({ value: rawKey, label: rawKey.charAt(0).toUpperCase() + rawKey.slice(1).replace(/_/g, ' ') });
        addedKeys.add(rawKey);
      }
    });

    if (sample.userdata && typeof sample.userdata === 'object') {
      Object.keys(sample.userdata).forEach(field => {
        if (!field || typeof sample.userdata[field] === 'object') return;
        cols.push({
          value: 'userdata.' + field,
          label: field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ') + ' (Custom)',
        });
      });
    }
    setAvailableColumns(cols);
  }, [contacts]);

  /* ════════════════════════════════════════════ */
  /* Extract template variables                  */
  /* ════════════════════════════════════════════ */
  useEffect(() => {
    if (!selectedTemplate) { setTemplateVariables([]); setMapping({}); return; }
    
    // ALWAYS extract variables from ALL text fields to ensure nothing is missed
    // The pre-defined variables array may be incomplete (e.g., missing header title vars)
    var text = [
      // Subject
      selectedTemplate.subject,
      // Header title - all possible paths (camelCase, snake_case, nested)
      selectedTemplate.headerTitle,
      selectedTemplate.header_title,
      selectedTemplate.header && selectedTemplate.header.title,
      // Body greeting - all possible paths
      selectedTemplate.bodyGreeting,
      selectedTemplate.body_greeting,
      selectedTemplate.body && selectedTemplate.body.greeting,
      // Body content - all possible paths
      selectedTemplate.bodyContent,
      selectedTemplate.body_content,
      selectedTemplate.body && selectedTemplate.body.content,
      selectedTemplate.body_html,
      selectedTemplate.bodyHtml,
      selectedTemplate.body_text,
      selectedTemplate.bodyText,
      // CTA button text/url (may contain variables)
      selectedTemplate.ctaButtonText,
      selectedTemplate.cta_button_text,
      selectedTemplate.cta && selectedTemplate.cta.button_text,
      selectedTemplate.ctaButtonUrl,
      selectedTemplate.cta && selectedTemplate.cta.button_url,
      // Footer fields (may contain variables)
      selectedTemplate.footerSignature,
      selectedTemplate.footer_signature,
      selectedTemplate.footer && selectedTemplate.footer.signature,
      selectedTemplate.footerSlogan,
      selectedTemplate.footer_slogan,
      selectedTemplate.footer && selectedTemplate.footer.slogan,
      selectedTemplate.footerCompanyAddress,
      selectedTemplate.footer && selectedTemplate.footer.company_address,
    ].filter(Boolean).join(' ');
    
    var extractedKeys = [];
    var matches = text.match(/\{\{(\w+)\}\}/g);
    if (matches) {
      extractedKeys = Array.from(new Set(matches.map(function(m) { return m.replace(/[{}]/g, ''); })));
    }
    
    // Build a type lookup from pre-defined variables (if available) for type hints
    var typeMap = {};
    if (selectedTemplate.variables && Array.isArray(selectedTemplate.variables)) {
      selectedTemplate.variables.forEach(function(v) {
        var key = typeof v === 'string' ? v : (v.key || v.name || '');
        var type = typeof v === 'object' ? (v.type || 'name') : 'name';
        if (key) typeMap[key] = type;
      });
    }
    
    // Merge: use extracted keys (complete scan) + type hints from pre-defined array
    var vars = extractedKeys.map(function(key) {
      return { key: key, type: typeMap[key] || 'name' };
    });
    
    setTemplateVariables(vars);
    var allNumeric = vars.length > 0 && vars.every(function(v) { return /^\d+$/.test(v.key); });
    setVariableType(allNumeric ? 'number' : 'name');
    // In edit mode the mapping is restored from the campaign API before this
    // effect runs. Skip the reset once, then allow normal resets on template changes.
    if (preserveMappingRef.current) {
      preserveMappingRef.current = false;
    } else {
      setMapping({});
    }
  }, [selectedTemplate]);

  /* ════════════════════════════════════════════ */
  /* Resolve value from contact                  */
  /* ════════════════════════════════════════════ */
  const resolveValue = useCallback((contact, column) => {
    if (!contact || !column) return '';
    if (column.startsWith('userdata.')) {
      const field = column.split('.').slice(1).join('.');
      return contact.userdata ? (contact.userdata[field] || '') : '';
    }
    return contact[column] || '';
  }, []);

  /* ════════════════════════════════════════════ */
  /* Handlers                                    */
  /* ════════════════════════════════════════════ */
  const onChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleTemplateSelect = async (tplId) => {
    if (!tplId) { setSelectedTemplate(null); return; }
    setTemplateLoading(true);
    try {
      // Use preview API — returns rendered HTML with signed image URLs
      const listTpl = templates.find(t => String(t.id) === String(tplId));
      const res = await templateApi.previewById(tplId);
      if (res.data?.success) {
        const d = res.data?.data;
        if (typeof d === 'string') {
          // Preview API returned rendered HTML directly
          setSelectedTemplate({ ...(listTpl || {}), html: d });
        } else if (d && typeof d === 'object') {
          // Preview API returned object (may include html field + template data)
          setSelectedTemplate({ ...(listTpl || {}), ...d });
        } else if (res.data?.html) {
          // html at response root
          setSelectedTemplate({ ...(listTpl || {}), html: res.data.html });
        } else {
          setSelectedTemplate(listTpl || null);
        }
      } else {
        setSelectedTemplate(listTpl || null);
      }
    } catch {
      setSelectedTemplate(templates.find(t => String(t.id) === String(tplId)) || null);
    } finally { setTemplateLoading(false); }
  };

  const handleBookSelect = (bookId) => {
    setSelectedBookId(bookId);
    setSelectedUserIds(new Set());
    setSelectionType('all');
    setContactSearch('');
    setContactPage(1);
    setSelectedLabel('');
  };

  const toggleUser = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleAllUsers = () => {
    if (selectedUserIds.size === contacts.length && contacts.length > 0) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(contacts.map(c => c.userid || c.id)));
    }
  };

  const handleMappingChange = (varKey, column) => {
    setMapping(prev => ({ ...prev, ['{{' + varKey + '}}']: column }));
  };

  /* ── Filtered templates (client-side removed — server handles search+pagination) ── */
  /* templates state is now set directly from fetchTemplates */

  /* ── Preview HTML ───────────────────────── */
  const firstContact = contacts[0];
  const previewHtml = useMemo(
    () => buildPreviewHtml(selectedTemplate, mapping, firstContact, resolveValue),
    [selectedTemplate, mapping, firstContact, resolveValue]
  );

  /* ── Smooth iframe updates ──────────────── */
  /* (Using srcDoc directly — ref-based approach removed) */

  const totalContactPages = Math.ceil(contactsTotal / CONTACTS_PER_PAGE) || 1;
  const selBook = addressBooks.find(a => (a.bookid || a.id) === selectedBookId);

  /* ═══════════════════════════════════════════ */
  /* Quota calculation (plan-based)              */
  /* ═══════════════════════════════════════════ */
  const recipientCount = selectionType === 'all' ? contactsTotal : selectedUserIds.size;
  const hasInsufficientQuota = (planQuota?.email?.remaining || 0) < recipientCount;

  /* ════════════════════════════════════════════ */
  /* Step validation                             */
  /* ════════════════════════════════════════════ */
  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.name.trim()) e.name = 'Campaign name is required';
    } else if (s === 1) {
      if (!selectedTemplate) e.template = 'Select a template';
    } else if (s === 2) {
      if (!selectedBookId) e.book = 'Select an address book';
      if (selectionType === 'selection' && selectedUserIds.size === 0) e.users = 'Select at least one contact';
    } else if (s === 3) {
      const unmapped = templateVariables.filter(v => !mapping['{{' + v.key + '}}']);
      if (unmapped.length > 0) e.mapping = 'Map all variables: ' + unmapped.map(v => v.key).join(', ');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => { if (!validateStep(step)) return; setStep(Math.min(step + 1, STEPS.length - 1)); };
  const prevStep = () => setStep(Math.max(step - 1, 0));

  /* ════════════════════════════════════════════ */
  /* Build payload + Create                      */
  /* ════════════════════════════════════════════ */
  const buildPayload = () => {
    // Detect which template fields contain variables so the backend resolves them all
    const variableFields = [];
    if (selectedTemplate) {
      const hasVars = (val) => val && /\{\{\w+\}\}/.test(val);
      if (hasVars(selectedTemplate.subject)) variableFields.push('subject');
      // Header title
      const hTitle = selectedTemplate.headerTitle || selectedTemplate.header_title
        || (selectedTemplate.header && selectedTemplate.header.title);
      if (hasVars(hTitle)) variableFields.push('header.title');
      // Body greeting
      const bGreeting = selectedTemplate.bodyGreeting || selectedTemplate.body_greeting
        || (selectedTemplate.body && selectedTemplate.body.greeting);
      if (hasVars(bGreeting)) variableFields.push('body.greeting');
      // Body content
      const bContent = selectedTemplate.bodyContent || selectedTemplate.body_content
        || (selectedTemplate.body && selectedTemplate.body.content);
      if (hasVars(bContent)) variableFields.push('body.content');
      // CTA
      const ctaText = selectedTemplate.ctaButtonText || selectedTemplate.cta_button_text
        || (selectedTemplate.cta && selectedTemplate.cta.button_text);
      if (hasVars(ctaText)) variableFields.push('cta.button_text');
      const ctaUrl = selectedTemplate.ctaButtonUrl
        || (selectedTemplate.cta && selectedTemplate.cta.button_url);
      if (hasVars(ctaUrl)) variableFields.push('cta.button_url');
      // Footer
      const fSig = selectedTemplate.footerSignature || selectedTemplate.footer_signature
        || (selectedTemplate.footer && selectedTemplate.footer.signature);
      if (hasVars(fSig)) variableFields.push('footer.signature');
    }

    const payload = {
      name: form.name,
      templateId: selectedTemplate?.id || undefined,
      addressbookId: selectedBookId || undefined,
      subject: selectedTemplate ? (selectedTemplate.subject || '') : '',
      selectionType: selectionType,
      userids: selectionType === 'selection' ? Array.from(selectedUserIds) : [],
      templatemapping: { variabletype: variableType, mapping: mapping },
    };
    // Tell backend which template fields contain variables (especially header.title)
    if (variableFields.length > 0) {
      payload.templatemapping.variableFields = variableFields;
    }
    // Sending mode — tells backend how to build the From address
    // "verified_domain"  → {customFromPrefix}@{customDomain}
    // "custom_from"      → {senderHandle}@mail.messageos.io
    // "platform_domain"  → info@messageos.io (fallback)
    if (sendingDomain === 'custom' && selectedCustomDomain) {
      payload.sendingMode = 'verified_domain';
      payload.selectedDomain = selectedCustomDomain.domain;   // backend resolves from_email via enterprise_domains
      payload.fromDomain = selectedCustomDomain.domain;       // kept for backward compatibility
    } else if (fromPreview?.senderHandle && fromPreview.senderHandle !== 'info') {
      payload.sendingMode = 'custom_from';
    } else {
      payload.sendingMode = 'platform_domain';
    }
    // Backend uses sender profile / domain settings for from_name and reply_to
    if (form.scheduleTime) payload.scheduleTime = new Date(form.scheduleTime).toISOString();
    return payload;
  };

  const handleCreate = async () => {
    for (var i = 0; i < STEPS.length - 1; i++) {
      if (!validateStep(i)) { setStep(i); toast.error('Please complete all required fields'); return; }
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = isEditMode
        ? await campaignApi.update(campaignId, payload)
        : await campaignApi.create(payload);
      if (res.data?.success) {
        toast.success(isEditMode ? 'Campaign updated!' : 'Campaign created!');
        const navId = res.data.data?.campaign_uuid || res.data.data?.id || campaignId;
        navigate('/dashboard/email/campaigns/' + navId);
      } else {
        toast.error(res.data?.error || (isEditMode ? 'Failed to update' : 'Failed to create'));
      }
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.error) || 'Failed');
    } finally { setSaving(false); }
  };

  /* ══════════════════════════════════════════ */
  /*  RENDER                                    */
  /* ══════════════════════════════════════════ */
  return (
    <div className="emcc-page">
      {/* Top */}
      <div className="emcc-top">
        <div className="emcc-top-left">
          <button className="em-btn em-btn-ghost" onClick={() => navigate('/dashboard/email/campaigns')}>
            <ArrowLeft size={16} /> Back
          </button>
          <h2 className="emcc-page-title">{isEditMode ? 'Edit Campaign' : 'New Campaign'}</h2>
        </div>

      </div>

      {editLoading ? (
        <div className="emcc-loading" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <p>Loading campaign data...</p>
        </div>
      ) : (
        <>
          {/* Stepper */}
          <TemplateWizardStepIndicator
            steps={STEPS.map((s, i) => ({ number: i + 1, title: s.label, key: s.key }))}
            currentStep={step + 1}
            onStepClick={(num) => { if (num < step + 1) setStep(num - 1); }}
            isStepCompleted={(num) => num < step + 1}
            maxWidth="100%"
          />

          {/* Step Body */}
          <div className="emcc-body">

        {/* ═════════════════════════════════════ */}
        {/* Step 0: Campaign Info                 */}
        {/* ═════════════════════════════════════ */}
        {step === 0 && (
          <div className="em-card">
            <h3 className="emcc-section-title">Campaign Details</h3>
            <div className="emcc-campaign-info-grid">
              <div className="em-input-group" style={{ marginBottom: 0 }}>
                <label className="em-label">Campaign Name *</label>
                <input
                  className={'em-input' + (errors.name ? ' em-input-error' : '')}
                  value={form.name}
                  onChange={e => onChange('name', e.target.value)}
                  placeholder="e.g. March Newsletter"
                  autoFocus
                />
                {errors.name && <p className="em-error-text">{errors.name}</p>}
              </div>
              <div className="em-input-group" style={{ marginBottom: 0 }}>
                <label className="em-label">
                  Schedule <span className="em-text-xs em-text-muted">(leave empty to send after creation)</span>
                </label>
                <input
                  className="em-input"
                  type="datetime-local"
                  value={form.scheduleTime}
                  onChange={e => onChange('scheduleTime', e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            </div>
            {/* Sending From — Domain Toggle */}
            <div className="emcc-sending-mode">
              <div className="emcc-sending-mode-header">
                <Mail size={14} />
                <span className="em-label" style={{ margin: 0, fontWeight: 600 }}>Sending From</span>
              </div>

              {!fromPreview ? (
                <div className="emcc-loading-inline">
                  <Loader2 size={16} className="spin-icon" />
                  <span>Loading sender profile...</span>
                </div>
              ) : (
                <>
                  {/* Domain Toggle Cards — only verified domains are selectable */}
                  {(() => {
                    const verifiedDomains = customDomains.filter(dm =>
                      dm.verificationStatus === 'Success' || dm.verificationStatus === 'verified'
                    );
                    const pendingDomains = customDomains.filter(dm =>
                      dm.verificationStatus === 'Pending' || dm.verificationStatus === 'pending'
                      || dm.verificationStatus === 'Failed' || dm.verificationStatus === 'failed'
                    );

                    return (
                      <>
                        <div className="emcc-domain-toggle-cards">
                          {/* Shared Domain Card */}
                          <button
                            type="button"
                            className={`emcc-domain-card ${sendingDomain === 'shared' ? 'emcc-domain-card--active' : ''}`}
                            onClick={() => setSendingDomain('shared')}
                          >
                            <div className="emcc-domain-card-radio">
                              <span className={`emcc-domain-radio ${sendingDomain === 'shared' ? 'emcc-domain-radio--selected' : ''}`} />
                            </div>
                            <div className="emcc-domain-card-icon" style={{ background: 'var(--em-success-l, #dcfce7)', color: 'var(--em-success, #16a34a)' }}>
                              <Zap size={18} />
                            </div>
                            <div className="emcc-domain-card-info">
                              <span className="emcc-domain-card-title">Shared Domain</span>
                              <span className="emcc-domain-card-address">
                                {fromPreview.senderHandle || 'info'}@{fromPreview.sharedDomain}
                              </span>
                              {fromPreview.senderName && (
                                <span className="emcc-domain-card-desc">From: {fromPreview.senderName}</span>
                              )}
                            </div>
                          </button>

                          {/* Verified Custom Domain Cards */}
                          {verifiedDomains.map((dm) => {
                            const isSelected = sendingDomain === 'custom' && selectedCustomDomain?.domain === dm.domain;
                            const prefix = dm.customFromPrefix || 'info';

                            return (
                              <button
                                key={dm.domain}
                                type="button"
                                className={`emcc-domain-card ${isSelected ? 'emcc-domain-card--active' : ''}`}
                                onClick={() => {
                                  setSendingDomain('custom');
                                  setSelectedCustomDomain(dm);
                                }}
                              >
                                <div className="emcc-domain-card-radio">
                                  <span className={`emcc-domain-radio ${isSelected ? 'emcc-domain-radio--selected' : ''}`} />
                                </div>
                                <div className="emcc-domain-card-icon" style={{ background: 'var(--em-primary-l, #ede9fe)', color: 'var(--em-primary, #6366f1)' }}>
                                  <Globe size={18} />
                                </div>
                                <div className="emcc-domain-card-info">
                                  <span className="emcc-domain-card-title">
                                    {dm.domain}
                                    <CheckCircle size={12} style={{ color: 'var(--em-success)', marginLeft: 4 }} />
                                  </span>
                                  <span className="emcc-domain-card-address">
                                    {prefix}@{dm.domain}
                                  </span>
                                  {fromPreview.senderName && (
                                    <span className="emcc-domain-card-desc">From: {fromPreview.senderName}</span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {/* Pending / Failed domains info — shown below, not as selectable cards */}
                        {pendingDomains.length > 0 && (
                          <div className="emcc-pending-domains-note">
                            <AlertCircle size={13} />
                            <span>
                              {pendingDomains.length === 1
                                ? <><strong>{pendingDomains[0].domain}</strong> is pending verification and will be available once verified.</>
                                : <><strong>{pendingDomains.length} domains</strong> ({pendingDomains.map(d => d.domain).join(', ')}) are pending verification.</>
                              }
                            </span>
                          </div>
                        )}

                        {/* No domains at all */}
                        {customDomains.length === 0 && verifiedDomains.length === 0 && (
                          <p className="emcc-no-domains-note">
                            No custom domain configured.{' '}
                            <a href="#!" onClick={(e) => { e.preventDefault(); navigate('/dashboard/email/settings/custom-domain'); }}>Add one</a>
                          </p>
                        )}
                      </>
                    );
                  })()}

                  <div className="emcc-from-preview-actions">
                    <a href="#!" onClick={(e) => { e.preventDefault(); navigate('/dashboard/email/settings/sender-profile'); }} className="emcc-from-link">
                      Edit Sender Profile
                    </a>
                    <span className="emcc-from-link-sep">·</span>
                    <a href="#!" onClick={(e) => { e.preventDefault(); navigate('/dashboard/email/settings/custom-domain'); }} className="emcc-from-link">
                      Custom Domain Settings
                    </a>
                  </div>
                </>
              )}


            </div>
          </div>
        )}

        {/* ═════════════════════════════════════ */}
        {/* Step 1: Select Template (table list)  */}
        {/* ═════════════════════════════════════ */}
        {step === 1 && (
          <div className="em-card">
            <h3 className="emcc-section-title">Select Email Template</h3>

            {/* Search */}
            <div className="emcc-tpl-search-row">
              <div className="emcc-tpl-search-wrap">
                <Search size={14} className="emcc-tpl-search-icon" />
                <input
                  className="em-input emcc-tpl-search-input"
                  placeholder="Search templates..."
                  value={templateSearch}
                  onChange={e => { setTemplateSearch(e.target.value); setTplPage(1); }}
                />
                {templateSearch && (
                  <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm emcc-search-clear" onClick={() => { setTemplateSearch(''); setTplPage(1); }}>
                    <X size={12} />
                  </button>
                )}
              </div>
              <span className="em-text-xs em-text-muted">
                {tplPagination.total > 0
                  ? tplPagination.total + ' template' + (tplPagination.total !== 1 ? 's' : '')
                  : templates.length + ' template' + (templates.length !== 1 ? 's' : '')}
              </span>
            </div>

            {/* Template Table */}
            {tplListLoading ? (
              <div className="emcc-loading-inline"><Loader2 size={16} className="spin-icon" /> Loading templates...</div>
            ) : templates.length === 0 ? (
              <div className="emcc-no-data">
                <AlertCircle size={16} />
                <span>{templateSearch ? 'No templates match your search' : 'No active templates. Create one in Templates first.'}</span>
              </div>
            ) : (
              <>
                <div className="emcc-tpl-table-wrap">
                  <table className="emcc-tpl-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th>
                        <th>Template</th>
                        <th>Category</th>
                        <th>Variables</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map(t => {
                        const isSelected = selectedTemplate && String(selectedTemplate.id) === String(t.id);
                        return (
                          <tr
                            key={t.id}
                            className={isSelected ? 'selected' : ''}
                            onClick={() => handleTemplateSelect(t.id)}
                          >
                            <td>
                              <input type="radio" className="emcc-tpl-radio" checked={!!isSelected} readOnly />
                            </td>
                            <td>
                              <div className="emcc-tpl-name">{t.name}</div>
                              <div className="emcc-tpl-subject">{t.subject || 'No subject'}</div>
                            </td>
                            <td>
                              {t.category ? (
                                <span className="em-badge em-badge-neutral">{t.category.replace(/_/g, ' ')}</span>
                              ) : '\u2014'}
                            </td>
                            <td>
                              {t.variables && t.variables.length > 0 ? (
                                <span className="em-text-sm"><Variable size={12} style={{ verticalAlign: -2, marginRight: 3 }} />{t.variables.length}</span>
                              ) : <span className="em-text-muted">\u2014</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Template Pagination */}
                {tplPagination.totalPages > 1 && (
                  <div className="emcc-contact-pagination">
                    <span className="em-text-xs em-text-muted">
                      Page {tplPagination.page} of {tplPagination.totalPages}
                      {tplPagination.total > 0 && (' \u00B7 ' + tplPagination.total + ' total')}
                    </span>
                    <div className="emcc-contact-page-btns">
                      <button className="em-btn em-btn-ghost em-btn-sm" disabled={tplPagination.page <= 1}
                        onClick={() => setTplPage(p => p - 1)}>
                        <ChevronLeft size={14} />
                      </button>
                      {Array.from({ length: Math.min(5, tplPagination.totalPages) }, (_, i) => {
                        let p = i + 1;
                        if (tplPagination.totalPages > 5)
                          p = Math.max(1, Math.min(tplPagination.page - 2, tplPagination.totalPages - 4)) + i;
                        return (
                          <button
                            key={p}
                            className={'em-btn em-btn-ghost em-btn-sm' + (tplPagination.page === p ? ' em-btn-primary' : '')}
                            onClick={() => setTplPage(p)}
                          >{p}</button>
                        );
                      })}
                      <button className="em-btn em-btn-ghost em-btn-sm" disabled={tplPagination.page >= tplPagination.totalPages}
                        onClick={() => setTplPage(p => p + 1)}>
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {templateLoading && (
              <div className="emcc-loading-inline"><Loader2 size={16} className="spin-icon" /> Loading template details...</div>
            )}
            {errors.template && <p className="em-error-text" style={{ marginTop: 8 }}>{errors.template}</p>}

            {/* Selected template info bar with preview button */}
            {selectedTemplate && (
              <div className="emcc-tpl-preview-bar">
                <div className="emcc-tpl-preview-info">
                  <span className="emcc-tpl-preview-name">{selectedTemplate.name}</span>
                  <span className="emcc-tpl-preview-sub">{selectedTemplate.subject || 'No subject'}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  {selectedTemplate.category && (
                    <span className="em-badge em-badge-primary" style={{ fontSize: '0.75rem' }}>
                      {selectedTemplate.category.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); })}
                    </span>
                  )}
                  <button
                    className="em-btn em-btn-secondary em-btn-sm"
                    onClick={() => setShowTemplatePreview(!showTemplatePreview)}
                  >
                    <Eye size={14} /> {showTemplatePreview ? 'Hide' : 'Preview'}
                  </button>
                </div>
              </div>
            )}

            {/* Template Preview Modal */}
            {showTemplatePreview && selectedTemplate && (
              <div className="emcc-tpl-modal-overlay" onClick={() => setShowTemplatePreview(false)}>
                <div className="emcc-tpl-modal" onClick={e => e.stopPropagation()}>
                  <div className="emcc-tpl-modal-header">
                    <h3 className="emcc-tpl-modal-title">
                      <Eye size={16} /> Template Preview
                    </h3>
                    <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" onClick={() => setShowTemplatePreview(false)}>
                      <X size={18} />
                    </button>
                  </div>
                  <div className="emcc-tpl-modal-body">
                    <div className="emcc-preview-frame emcc-pv-desktop" style={{ maxWidth: 620, margin: '0 auto' }}>
                      <iframe
                        title="Template Preview"
                        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                        className="emcc-preview-iframe"
                        srcDoc={selectedTemplate.html ? appendAttachmentsToHtml(selectedTemplate.html, selectedTemplate) : buildPreviewHtml(selectedTemplate, {}, null, resolveValue)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═════════════════════════════════════ */}
        {/* Step 2: Address Book & Contacts       */}
        {/* ═════════════════════════════════════ */}
        {step === 2 && (
          <div className="em-card">
            <h3 className="emcc-section-title">
              <BookOpen size={18} style={{ marginRight: 8, verticalAlign: -3 }} />
              Select Address Book
            </h3>

            {loadingLists ? (
              <div className="em-skl" style={{ height: 44 }} />
            ) : addressBooks.length === 0 ? (
              <div className="emcc-no-data">
                <AlertCircle size={18} />
                <span>No address books found. Create one in Contacts first.</span>
              </div>
            ) : (
              <>
                {/* Address Book Cards */}
                <div className="emcc-books-grid">
                  {addressBooks.map(a => {
                    const bid = a.bookid || a.id;
                    const isSelected = selectedBookId === String(bid);
                    return (
                      <div
                        key={bid}
                        className={'emcc-book-card' + (isSelected ? ' selected' : '')}
                        onClick={() => handleBookSelect(String(bid))}
                      >
                        <div className="emcc-book-icon">
                          <Users size={18} />
                        </div>
                        <div className="emcc-book-info">
                          <div className="emcc-book-name">{a.bookname || a.name || 'Address Book'}</div>
                          <div className="emcc-book-count">
                            {a.totaluser != null ? Number(a.totaluser).toLocaleString() + ' contacts' : '\u2014'}
                          </div>
                        </div>
                        {isSelected && <CheckCircle size={18} className="emcc-book-check" />}
                      </div>
                    );
                  })}
                </div>
                {errors.book && <p className="em-error-text">{errors.book}</p>}
              </>
            )}

            {/* Selection type + contacts */}
            {selectedBookId && (
              <>
                <div className="emcc-sel-type" style={{ marginTop: '1rem' }}>
                  <label className="emcc-sel-radio">
                    <input type="radio" name="selType" value="all" checked={selectionType === 'all'}
                      onChange={() => { setSelectionType('all'); setSelectedUserIds(new Set()); }} />
                    <span>Send to all contacts ({contactsTotal.toLocaleString()})</span>
                  </label>
                  <label className="emcc-sel-radio">
                    <input type="radio" name="selType" value="selection" checked={selectionType === 'selection'}
                      onChange={() => setSelectionType('selection')} />
                    <span>Select specific contacts</span>
                  </label>
                </div>

                {selectionType === 'selection' && (
                  <>
                    {/* Filters row */}
                    <div className="emcc-contact-filters">
                      <div className="emcc-search-wrap">
                        <Search size={14} className="emcc-search-icon" />
                        <input
                          className="em-input emcc-search-input"
                          placeholder="Search by name, email, phone..."
                          value={contactSearch}
                          onChange={e => { setContactSearch(e.target.value); setContactPage(1); }}
                        />
                        {contactSearch && (
                          <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm emcc-search-clear" onClick={() => { setContactSearch(''); setContactPage(1); }}>
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      <select
                        className="em-select emcc-label-filter"
                        value={selectedLabel}
                        onChange={e => { setSelectedLabel(e.target.value); setContactPage(1); }}
                        disabled={labelsLoading}
                      >
                        <option value="">{labelsLoading ? 'Loading labels...' : 'All Labels'}</option>
                        {labels.map((lbl, i) => {
                          const labelVal = typeof lbl === 'string' ? lbl : (lbl.label || lbl.name || lbl.labelname || '');
                          return labelVal ? <option key={labelVal + i} value={labelVal}>{labelVal}</option> : null;
                        })}
                      </select>
                      {selectedUserIds.size > 0 && (
                        <span className="emcc-sel-count">{selectedUserIds.size} selected</span>
                      )}
                    </div>

                    {/* Contacts Table */}
                    {contactsLoading ? (
                      <div className="emcc-loading-inline"><Loader2 size={16} className="spin-icon" /> Loading contacts...</div>
                    ) : contacts.length === 0 ? (
                      <div className="emcc-no-data">
                        <AlertCircle size={16} />
                        <span>{contactSearch || selectedLabel ? 'No contacts match your filters' : 'No contacts in this address book'}</span>
                      </div>
                    ) : (
                      <>
                        <div className="emcc-contact-table-wrap">
                          <table className="emcc-contact-table">
                            <thead>
                              <tr>
                                <th style={{ width: 36 }}>
                                  <input type="checkbox"
                                    checked={selectedUserIds.size === contacts.length && contacts.length > 0}
                                    onChange={toggleAllUsers} />
                                </th>
                                {availableColumns.map(col => (
                                  <th key={col.value}>{col.label}</th>
                                ))}
                                {contacts[0] && contacts[0].label && <th>Label</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {contacts.map(c => {
                                const uid = c.userid || c.id;
                                return (
                                  <tr key={uid} className={selectedUserIds.has(uid) ? 'selected' : ''} onClick={() => toggleUser(uid)}>
                                    <td><input type="checkbox" checked={selectedUserIds.has(uid)} readOnly /></td>
                                    {availableColumns.map(col => (
                                      <td key={col.value}>
                                        {resolveValue(c, col.value) || <span className="em-text-muted">&mdash;</span>}
                                      </td>
                                    ))}
                                    {contacts[0] && contacts[0].label && <td><span className="em-badge em-badge-xs em-badge-neutral">{c.label || '\u2014'}</span></td>}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        <div className="emcc-contact-pagination">
                          <span className="em-text-xs em-text-muted">
                            Showing {((contactPage - 1) * CONTACTS_PER_PAGE) + 1}&ndash;{Math.min(contactPage * CONTACTS_PER_PAGE, contactsTotal)} of {contactsTotal}
                          </span>
                          <div className="emcc-contact-page-btns">
                            <button className="em-btn em-btn-ghost em-btn-sm" disabled={contactPage <= 1}
                              onClick={() => setContactPage(p => p - 1)}>&lsaquo; Prev</button>
                            <span className="em-text-xs" style={{ padding: '0 8px' }}>Page {contactPage} of {totalContactPages}</span>
                            <button className="em-btn em-btn-ghost em-btn-sm" disabled={contactPage >= totalContactPages}
                              onClick={() => setContactPage(p => p + 1)}>Next &rsaquo;</button>
                          </div>
                        </div>
                      </>
                    )}
                    {errors.users && <p className="em-error-text" style={{ marginTop: 8 }}>{errors.users}</p>}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ═════════════════════════════════════ */}
        {/* Step 3: Variable Mapping & Preview    */}
        {/* ═════════════════════════════════════ */}
        {step === 3 && (
          <div className="emcc-mapping-layout">
            <div className="emcc-mapping-main">
              <div className="em-card">
                <h3 className="emcc-section-title">
                  <Variable size={18} style={{ marginRight: 6, verticalAlign: -3 }} />
                  Map Template Variables
                </h3>
                <p className="em-text-sm em-text-muted" style={{ marginBottom: '1rem' }}>
                  Map each template variable to an address book column. Values are dynamically replaced for each recipient.
                </p>

                {templateVariables.length === 0 ? (
                  <div className="emcc-no-data">
                    <AlertCircle size={16} />
                    <span>This template has no variables. You can proceed.</span>
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="emcc-no-data">
                    <AlertCircle size={16} />
                    <span>No contacts loaded. Go back and select an address book first.</span>
                  </div>
                ) : (
                  <>
                    <div className="emcc-mapping-table">
                      <div className="emcc-map-row emcc-map-header">
                        <span>Template Variable</span>
                        <span></span>
                        <span>Address Book Column</span>
                        <span>Sample Value</span>
                      </div>
                      {templateVariables.map(v => {
                        const col = mapping['{{' + v.key + '}}'] || '';
                        const sampleVal = col ? resolveValue(firstContact, col) : '';
                        return (
                          <div key={v.key} className="emcc-map-row">
                            <div className="emcc-map-var">
                              <code>{'{{' + v.key + '}}'}</code>
                              <span className={'em-badge em-badge-xs em-badge-' + (v.type === 'number' ? 'info' : 'success')}>{v.type}</span>
                            </div>
                            <span className="emcc-map-arrow">&rarr;</span>
                            <select
                              className={'em-select em-select-sm' + (!col && errors.mapping ? ' em-input-error' : '')}
                              value={col}
                              onChange={e => handleMappingChange(v.key, e.target.value)}
                            >
                              <option value="">Select column...</option>
                              {availableColumns.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                            <span className="emcc-map-sample">
                              {sampleVal || <span className="em-text-muted">&mdash;</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {errors.mapping && <p className="em-error-text" style={{ marginTop: 8 }}>{errors.mapping}</p>}
                    {firstContact && (
                      <p className="em-text-xs em-text-muted" style={{ marginTop: '0.75rem' }}>
                        Sample values from: <strong>{firstContact.username || firstContact.emailid || 'Contact #1'}</strong>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Live Preview */}
            <div className="emcc-mapping-preview">
              <div className="em-card">
                <div className="emcc-preview-header">
                  <h3 className="em-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Eye size={16} /> Live Preview
                  </h3>
                  <div className="emcc-pv-toggle">
                    <button className={'emcc-pv-btn' + (previewMode === 'desktop' ? ' active' : '')} onClick={() => setPreviewMode('desktop')}>
                      <Monitor size={12} /> Desktop
                    </button>
                    <button className={'emcc-pv-btn' + (previewMode === 'mobile' ? ' active' : '')} onClick={() => setPreviewMode('mobile')}>
                      <Smartphone size={12} /> Mobile
                    </button>
                  </div>
                </div>
                <div className={'emcc-preview-frame emcc-pv-' + previewMode}>
                  <iframe
                    ref={previewIframeRef}
                    title="Campaign Preview"
                    sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    className="emcc-preview-iframe"
                    srcDoc={previewHtml}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═════════════════════════════════════ */}
        {/* Step 4: Review                        */}
        {/* ═════════════════════════════════════ */}
        {step === 4 && (
          <div className="emcc-review-layout">
            <div className="emcc-review-main">
              <div className="em-card">
                {/* Unsubscribe info banner */}
                {selectedTemplate && selectedTemplate.category && selectedTemplate.category !== 'transactional' && (
                  <div className="emcc-info-banner">
                    <Info size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                      Recipients who have unsubscribed from &ldquo;{selectedTemplate.category.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); })}&rdquo; will be automatically skipped.
                    </span>
                  </div>
                )}
                <h3 className="emcc-section-title">Review Campaign</h3>
                <div className="emcc-review-grid">
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Campaign Name</span>
                    <span className="emcc-review-value">{form.name || '\u2014'}</span>
                  </div>
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Template</span>
                    <span className="emcc-review-value">{selectedTemplate ? selectedTemplate.name : '\u2014'}</span>
                  </div>
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Subject</span>
                    <span className="emcc-review-value">{selectedTemplate ? (selectedTemplate.subject || '\u2014') : '\u2014'}</span>
                  </div>
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Sending From</span>
                    <span className="emcc-review-value">
                      {sendingDomain === 'custom' && selectedCustomDomain
                        ? `${fromPreview?.senderName ? fromPreview.senderName + ' <' : ''}${selectedCustomDomain.customFromPrefix || 'info'}@${selectedCustomDomain.domain}${fromPreview?.senderName ? '>' : ''}`
                        : fromPreview?.sharedFrom || 'Using sender profile defaults'}
                    </span>
                  </div>
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Domain</span>
                    <span className="emcc-review-value">
                      {sendingDomain === 'custom' && selectedCustomDomain
                        ? `Custom Domain (${selectedCustomDomain.domain})`
                        : `Shared Domain (${fromPreview?.sharedDomain || 'mail.messageos.io'})`}
                    </span>
                  </div>
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Sender</span>
                    <span className="emcc-review-value">
                      <span style={{ color: '#888' }}>{fromPreview?.senderName || 'From sender profile / domain settings'}</span>
                    </span>
                  </div>
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Address Book</span>
                    <span className="emcc-review-value">
                      {selBook ? (selBook.bookname || selBook.name || '\u2014') : '\u2014'}
                      {selBook && selBook.totaluser != null ? ' (' + selBook.totaluser + ' contacts)' : ''}
                    </span>
                  </div>
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Recipients</span>
                    <span className="emcc-review-value">
                      {selectionType === 'all'
                        ? 'All contacts (' + contactsTotal.toLocaleString() + ')'
                        : selectedUserIds.size + ' selected contacts'}
                    </span>
                  </div>
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Schedule</span>
                    <span className="emcc-review-value">
                      {form.scheduleTime ? new Date(form.scheduleTime).toLocaleString() : 'Not scheduled (manual send)'}
                    </span>
                  </div>
                  <div className="emcc-review-item">
                    <span className="emcc-review-label">Variables Mapped</span>
                    <span className="emcc-review-value">
                      {templateVariables.length === 0
                        ? 'No variables'
                        : Object.keys(mapping).filter(function(k) { return mapping[k]; }).length + ' / ' + templateVariables.length}
                    </span>
                  </div>
                </div>

                {/* Email Quota Info */}
                {selectedBookId && (
                  <div className="emcc-cost-section">
                    <div className="emcc-cost-cards">
                      <div className="emcc-cost-card">
                        <div className="emcc-cost-card-icon" style={{ background: '#f0f4ff', color: '#4f46e5' }}>
                          <Mail size={18} />
                        </div>
                        <div className="emcc-cost-card-info">
                          <span className="emcc-cost-card-label">Recipients</span>
                          <span className="emcc-cost-card-value">{recipientCount.toLocaleString()}</span>
                        </div>
                      </div>
                      {/* Plan-based quota display */}
                      <div className={`emcc-cost-card ${hasInsufficientQuota ? 'emcc-cost-card-warning' : ''}`}>
                        <div className="emcc-cost-card-icon" style={{ background: hasInsufficientQuota ? '#fef2f2' : '#ecfdf5', color: hasInsufficientQuota ? '#ef4444' : '#10b981' }}>
                          <Mail size={18} />
                        </div>
                        <div className="emcc-cost-card-info">
                          <span className="emcc-cost-card-label">Email Quota</span>
                          <span className="emcc-cost-card-value" style={{ color: hasInsufficientQuota ? '#ef4444' : undefined }}>
                            {planQuotaLoading ? '...' : `${(planQuota?.email?.remaining || 0).toLocaleString('en-IN')} remaining`}
                          </span>
                        </div>
                      </div>
                      {recipientCount > 0 && (
                        <div className="emcc-cost-card">
                          <div className="emcc-cost-card-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>
                            <Users size={18} />
                          </div>
                          <div className="emcc-cost-card-info">
                            <span className="emcc-cost-card-label">After Sending</span>
                            <span className="emcc-cost-card-value">{Math.max(0, (planQuota?.email?.remaining || 0) - recipientCount).toLocaleString('en-IN')} remaining</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {hasInsufficientQuota && (
                      <div className="emcc-insufficient-warning">
                        <AlertCircle size={15} />
                        <span>Insufficient email quota. You need {recipientCount.toLocaleString('en-IN')} emails but only {(planQuota?.email?.remaining || 0).toLocaleString('en-IN')} remain.</span>
                        <button className="em-btn em-btn-sm emcp-buy-btn" onClick={() => navigate('/dashboard/pricing')}>
                          <CreditCard size={12} />
                          <span>Upgrade Plan</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Mapping Summary */}
                {templateVariables.length > 0 && (
                  <div className="emcc-review-mapping">
                    <h4 className="emcc-review-sublabel">Variable Mapping</h4>
                    <div className="emcc-review-map-list">
                      {templateVariables.map(v => {
                        const col = mapping['{{' + v.key + '}}'];
                        const colLabel = availableColumns.find(function(c) { return c.value === col; });
                        return (
                          <div key={v.key} className="emcc-review-map-item">
                            <code>{'{{' + v.key + '}}'}</code>
                            <span className="emcc-review-map-arrow">&rarr;</span>
                            <span className={col ? '' : 'em-text-danger'}>{colLabel ? colLabel.label : (col || 'Not mapped')}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Review Preview */}
            <div className="emcc-review-preview">
              <div className="em-card">
                <h3 className="em-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.75rem' }}>
                  <Mail size={16} /> Email Preview
                </h3>
                <div className="emcc-preview-frame emcc-pv-desktop" style={{ maxWidth: '100%' }}>
                  <iframe
                    title="Final Preview"
                    sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    className="emcc-preview-iframe"
                    srcDoc={previewHtml}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Navigation */}
      <div className="emcc-footer">
        <div>
          {step > 0 && (
            <button className="em-btn em-btn-secondary" onClick={prevStep}>
              <ArrowLeft size={16} /> Previous
            </button>
          )}
        </div>
        <div className="emcc-footer-right">
          {step < STEPS.length - 1 ? (
            <button className="em-btn em-btn-primary" onClick={nextStep}>
              Next <ArrowRight size={16} />
            </button>
          ) : (
            <button className="em-btn em-btn-primary" onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 size={16} className="spin-icon" /> : <Check size={16} />}
              <span>{isEditMode ? 'Update Campaign' : 'Create Campaign'}</span>
            </button>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default EmailCampaignCreate;
